import { React } from 'Imports';
import { useCallback, useEffect, useState } from 'react';
import { VehicleCameraPairingResponse } from '$Generated/api';
import { isEncompass } from '$Utilities/integrationPlatform';
import { DeviceAssociationService } from '$State/DeviceAssociationFreezerService';
import { RequestVideoHeader } from '../RequestVideoHeader';
import { SingleDatePickerCalendar } from './SingleDatePickerCalendar';
import { OfflineCameraBanner } from './OfflineCameraBanner';
import { faCircleCheck, faCircleXmark, faSpinner, faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

const styles = require('./SelectDateStep.scss') as {
    content: string;
    titleDescriptionWrapper: string;
    verbiage: string;
    retryLink: string;
    calendarContainer: string;
    calendarContainerEncompass: string;
    success: string;
    error: string;
    empty: string;
    loading: string;
    status: string;
    statusGeotab: string;
    statusEncompass: string;
};

interface ISelectDateStepProps {
    pairing: VehicleCameraPairingResponse;
    selectedDate?: Date;
    onDateChange: (date: Date) => void;
}

type FootageDatesStatus = 'loading' | 'success' | 'empty' | 'error';

export const SelectDateStep = (props: ISelectDateStepProps) => {
    const { pairing, selectedDate, onDateChange } = props;

    const isOnline = pairing.cameraDevice?.onlineStatus?.toLowerCase() === 'online';
    const serialNumber = pairing.cameraDevice?.serialNumber ?? '';

    const getValidCachedDates = () => {
        const state = DeviceAssociationService.getState();
        if (state.cameraFootageDatesSerialNumber === serialNumber) return state.cameraFootageDates?.data ?? null;
        return null;
    };

    const cachedDates = getValidCachedDates();

    const [datesWithAvailableFootage, setDatesWithAvailableFootage] = useState<Date[]>(cachedDates ?? []);
    const [status, setStatus] = useState<FootageDatesStatus>(() => {
        if (!isOnline || !serialNumber) return 'loading';
        if (cachedDates) return cachedDates.length > 0 ? 'success' : 'empty';
        return 'loading';
    });

    const loadFootageDates = useCallback(() => {
        if (!isOnline || !serialNumber) return;

        const state = DeviceAssociationService.getState();
        if (state.cameraFootageDatesSerialNumber === serialNumber && state.cameraFootageDates?.data) {
            setDatesWithAvailableFootage(state.cameraFootageDates.data);
            setStatus(state.cameraFootageDates.data.length > 0 ? 'success' : 'empty');
            return;
        }

        setDatesWithAvailableFootage([]);
        setStatus('loading');
        DeviceAssociationService.getFootageDates(serialNumber)
            .then(() => {
                const ajaxState = DeviceAssociationService.getState().cameraFootageDates;
                if (!ajaxState?.data) {
                    setStatus('error');
                    return;
                }
                setDatesWithAvailableFootage(ajaxState.data);
                setStatus(ajaxState.data.length > 0 ? 'success' : 'empty');
            })
            .catch(() => setStatus('error'));
    }, [isOnline, serialNumber]);

    useEffect(() => {
        if (!isOnline || !serialNumber) {
            setDatesWithAvailableFootage([]);
            setStatus('loading');
            return;
        }
        loadFootageDates();
    }, [isOnline, serialNumber, loadFootageDates]);

    const renderAvailableFootageStatus = () => {
        const loading = (
            <div className={styles.loading}>
                <FontAwesomeIcon icon={faSpinner} spin /> Checking for dates with available footage...
            </div>
        );

        const success = (
            <div className={styles.success}>
                <FontAwesomeIcon icon={faCircleCheck} /> Dates with available footage are highlighted.
            </div>
        );

        const empty = (
            <div className={styles.empty}>
                <FontAwesomeIcon icon={faCircleXmark} /> No dates with available footage found.
            </div>
        );

        const error = (
            <div className={styles.error}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>
                    Request timed out.{' '}
                    <button type="button" className={styles.retryLink} onClick={loadFootageDates}>
                        Try again
                    </button>{' '}
                    or select a date and proceed.
                </span>
            </div>
        );

        return (
            <div className={classNames(styles.status, isEncompass ? styles.statusEncompass : styles.statusGeotab)}>
                {status === 'loading' && loading}
                {status === 'success' && success}
                {status === 'empty' && empty}
                {status === 'error' && error}
            </div>
        );
    };

    return (
        <>
            <RequestVideoHeader pairing={pairing} selectedDate={selectedDate} />
            <div className={styles.content}>
                {!isOnline && <OfflineCameraBanner />}
                <div className={styles.titleDescriptionWrapper}>
                    <b>Select a Date</b>
                    {isOnline && renderAvailableFootageStatus()}
                </div>
                <div
                    className={isEncompass ? `${styles.calendarContainer} ${styles.calendarContainerEncompass}` : styles.calendarContainer}
                >
                    <SingleDatePickerCalendar date={selectedDate} onDateChange={onDateChange} availableDates={datesWithAvailableFootage} />
                </div>
            </div>
        </>
    );
};
